import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import {
  AppBar,
  Box,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { useAuth } from '../auth/AuthContext';
import { ThemeModeToggle } from '../components/ThemeModeToggle';

import { NAVIGATION, type NavigationGroup, type NavigationItem } from './navigation';

const DRAWER_WIDTH = 244;

/**
 * Below this the sidebar becomes a drawer you open rather than one that is always there.
 *
 * A permanent 244px rail on a 420px screen leaves 176px for a table of nine columns, which is not a
 * compromised layout — it is an unusable one. Staff tooling is desktop-first and that is fine; being
 * *broken* on a phone is not the same as being unoptimised for one.
 */
const COLLAPSE_BELOW = 'md';

export const AppLayout = () => {
  const { account, signOut } = useAuth();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down(COLLAPSE_BELOW));
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const groups: NavigationGroup[] = NAVIGATION.map((group) => ({
    heading: group.heading,
    items: group.items.filter((item) => (account ? item.allow.includes(account.role) : false)),
  })).filter((group) => group.items.length > 0);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1.5, minHeight: { xs: 58, sm: 58 } }}>
          {isNarrow ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Otvoriť menu"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <PsychologyOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="h6" sx={{ letterSpacing: '-0.015em' }}>
            MysliKrok
          </Typography>
          <Chip size="small" label="back office" variant="outlined" sx={{ ml: 0.5 }} />

          <Box sx={{ flexGrow: 1 }} />

          <ThemeModeToggle />

          {account ? (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />
              <Stack sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2" sx={{ lineHeight: 1.25, fontWeight: 560 }}>
                  {account.displayName ?? account.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                  {account.role}
                </Typography>
              </Stack>
              <Tooltip title="Odhlásiť sa">
                <IconButton
                  size="small"
                  aria-label="Odhlásiť sa"
                  onClick={() => {
                    void signOut().then(() => navigate('/login'));
                  }}
                >
                  <LogoutOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isNarrow ? 'temporary' : 'permanent'}
        open={isNarrow ? isDrawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isNarrow ? 0 : DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 58, sm: 58 } }} />
        <Box sx={{ px: 1.5, py: 2, overflowY: 'auto' }}>
          {groups.map((group, groupIndex) => (
            <Box key={group.heading ?? `group-${groupIndex}`} sx={{ mb: 1.5 }}>
              {group.heading ? (
                <Typography
                  variant="overline"
                  color="text.disabled"
                  sx={{ px: 1.5, display: 'block', mb: 0.25 }}
                >
                  {group.heading}
                </Typography>
              ) : null}
              <List disablePadding>
                {group.items.map((item: NavigationItem) => {
                  const Icon = item.icon;
                  const selected = location.pathname === item.path;

                  return (
                    <ListItemButton
                      key={item.path}
                      selected={selected}
                      onClick={() => {
                        navigate(item.path);
                        setDrawerOpen(false);
                      }}
                      sx={{ mb: 0.25 }}
                    >
                      <ListItemIcon sx={{ color: selected ? 'primary.main' : 'text.secondary' }}>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: selected ? 620 : 500,
                          color: selected ? 'text.primary' : 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 'auto', p: 2 }}>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1.45 }}>
            Predplatné sa zatiaľ nevymáha — každý účet má plný prístup.
          </Typography>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', minWidth: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 58, sm: 58 } }} />
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};
